import { DEFAULT_PMCHATSTATE, PMSTATE, SETTINGSSTATE, USERSSTATE } from '..'
import { PMChatState } from '../../../types/state'
import { goToMessage } from '../../actions/nav'
import { SettingsType } from '../../constants/enums'
import { avatarUrl } from '../../mfcLogic/avatarPreviewUrl'
import { MfcNotification } from '../../mfcLogic/notification'
import { pubSubMessages } from '../pubSub'
import { stateWrapper, stateWrapperShortform } from '../wrappers'
import { isQuickReplyOpen } from './floaters'

export const emitPMNotification = (id, text) => {
    if (!SETTINGSSTATE[SettingsType.DEFAULT].pmNotification) {
        return
    }

    const { slug, name = slug } = USERSSTATE.users[id] || {}

    if (!slug) {
        console.warn(
            '[Message APP State]: ' +
                'Cannot notify user because of missing slug. ' +
                'This is not supposed to happen!',
        )
        return
    }

    void MfcNotification.new({
        message: 'Private Message from ' + name,
        icon: avatarUrl(id, 150),
        body: text,
        onclick: () => {
            void goToMessage(slug)
        },
    })
}

export const emitState = (id?) => {
    if (id) {
        pubSubMessages.emit(`state::${id}`, PMSTATE.chats[id], true)
        return
    }

    pubSubMessages.emit(`state`, { ...PMSTATE }, true)
}

export const updateAndEmitMessagesState = (id) => {
    const existingChat = PMSTATE.chats[id]
    if (!existingChat) return
    const { history, latest } = existingChat

    existingChat.messages = [...history, ...latest]

    pubSubMessages.emit(`state::messages::${id}`, PMSTATE.chats[id], true)
}

const sW = stateWrapper({ state: PMSTATE, update: emitState })
const sWSF = stateWrapperShortform({ state: PMSTATE, update: emitState })

export const setPMBatchLoadingState = sWSF('isBatchLoading')
export const setPMSearchingState = sWSF('isSearching')
export const setPMExaustedState = sWSF('isExhausted')
export const setPMActiveIDState = sWSF('activeID')
export const setPMLoadingState = sWSF('isLoading')

export const setPMUnreadState = sW((unreadDiff: number) => {
    const { unread: previousUnread = 0 } = PMSTATE
    PMSTATE.unread = previousUnread + unreadDiff
})

export const chatStateWrapper =
    (execute, key?, options = { diffInput: true, diffOutput: false }) =>
    (id, ...args) => {
        createChatRecord(id)

        const { diffInput = true, diffOutput } = options

        if (key && diffInput) {
            const [value] = args

            if (PMSTATE.chats[id][key] === value) {
                // don't update if it's the same value
                return
            }
        }

        const previousValue = PMSTATE.chats[id][key]

        execute(id, ...args)

        if (key && diffOutput && PMSTATE.chats[id][key] === previousValue) {
            // don't update if it's the same value
            return
        }

        PMSTATE.chats[id] = { ...PMSTATE.chats[id] }
        PMSTATE.chats = { ...PMSTATE.chats }

        if (PMSTATE.skipPubsub) {
            return
        }

        emitState(id)
    }

export const chatStateWrapperShortform = (key, options?) =>
    chatStateWrapper(
        (id: number, value: any) => {
            PMSTATE.chats[id][key] = value
        },
        key,
        options,
    )

export const setPMChatWasPMCommandUsedState =
    chatStateWrapperShortform('wasPMCommandUsed')

export const setPMChatLoadingState = chatStateWrapperShortform('isLoading')

export const setPMChatHistoryInitiatedState =
    chatStateWrapperShortform('isHistoryInitiated')

export const setPMChatExaustedState = chatStateWrapper(
    (id: number, isExhausted: boolean) => {
        const {
            chats: { [id]: existingChat = {} as PMChatState },
        } = PMSTATE

        existingChat.isExhausted = isExhausted
        existingChat.isHistoryUpdated = false
    },
)

export const setPMChatOpenState = chatStateWrapper(
    (id: number, isOpen: boolean) => {
        PMSTATE.chats[id].isOpen = isOpen

        if (isOpen) {
            setPMActiveIDState(id)
            setPMChatIntentionallyClosedState(id, false)
            return
        }

        setPMActiveIDState(null)
    },
    'isOpen',
)

export const setPMChatUnreadState = chatStateWrapper(
    (id: number, unread: number) => {
        const {
            chats: { [id]: chat },
        } = PMSTATE

        const { unread: previousUnread = 0, isOpen } = chat

        if ((isOpen || isQuickReplyOpen(id, true)) && unread > 0) {
            return
        }

        if (PMSTATE.unreadFragments[id]) {
            setPMUnreadState(-PMSTATE.unreadFragments[id])
            delete PMSTATE.unreadFragments[id]
        }

        PMSTATE.chats = {
            ...PMSTATE.chats,
            [id]: { ...PMSTATE.chats[id], unread },
        }

        setPMUnreadState(unread - previousUnread)
    },
    'unread',
)

export const setPMChatIntentionallyClosedState = chatStateWrapper(
    (id: number, isIntentionallyClosed: boolean) => {
        if (isIntentionallyClosed && PMSTATE.activeID === id) {
            setPMActiveIDState(null)
        }

        PMSTATE.chats[id].isIntentionallyClosed = isIntentionallyClosed
    },
    'isIntentionallyClosed',
)

export const setPMChatLatest = chatStateWrapper((id, message) => {
    const { system } = message
    const {
        chats: { [id]: existingChat },
        startOfTodayTimestamp,
    } = PMSTATE
    const { listHasToday, latest } = existingChat

    if (!listHasToday && !system) {
        existingChat.listHasToday = true
        latest.push({
            datetime: new Date(startOfTodayTimestamp).toDateString(),
            timestamp: startOfTodayTimestamp,
            marker: true,
            today: true,
        })
    }

    existingChat.latest = [...latest, message]
    existingChat.isHistoryUpdated = false

    updateAndEmitMessagesState(id)
})

export const setPMChatIsTypingState = chatStateWrapper(
    (id: number, isTyping: boolean) => {
        const {
            chats: { [id]: existingChat = {} as PMChatState },
        } = PMSTATE

        existingChat.isTyping = isTyping

        if (isTyping) {
            existingChat.isHistoryUpdated = false
        }
    },
)

export const createChatRecord = (id) => {
    if (PMSTATE.chats[id]) {
        return
    }

    PMSTATE.chats[id] = JSON.parse(JSON.stringify(DEFAULT_PMCHATSTATE))

    return PMSTATE.chats[id]
}
